import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Filters the homepage catalog without navigating to another collection.
   *
   * @param {Event} event
   */
  updateCategory(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-devarch-category-filter]');
    if (!(button instanceof HTMLButtonElement)) return;

    const category = button.dataset.devarchCategoryFilter;
    if (!category) return;

    const buttons = this.querySelectorAll('[data-devarch-category-filter]');
    const cards = this.querySelectorAll('[data-devarch-category-handles]');

    for (const categoryButton of buttons) {
      const isActive = categoryButton === button;
      categoryButton.classList.toggle('is-active', isActive);
      categoryButton.setAttribute('aria-pressed', String(isActive));
    }

    for (const card of cards) {
      const categories = card.dataset.devarchCategoryHandles?.split(/\s+/).filter(Boolean) ?? [];

      for (const animation of card.getAnimations()) {
        if (animation.id === 'devarch-category-card-in') animation.cancel();
      }

      card.hidden = category !== 'all' && !categories.includes(category);
    }

    let visibleIndex = 0;
    for (const card of cards) {
      if (!(card instanceof HTMLElement) || card.hidden) continue;

      const animation = card.animate(
        [
          { opacity: 0, transform: 'translateY(10px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        {
          duration: 320,
          delay: Math.min(visibleIndex, 5) * 35,
          easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
          fill: 'both',
        }
      );

      animation.id = 'devarch-category-card-in';
      animation.finished.then(
        () => animation.cancel(),
        () => {}
      );
      visibleIndex += 1;
    }

    const count = this.querySelector('[data-devarch-catalog-count]');
    if (count && button.dataset.devarchItemCountLabel) {
      count.textContent = button.dataset.devarchItemCountLabel;
    }

    const heading = document.querySelector('[data-devarch-home-heading]');
    if (heading) {
      heading.textContent =
        category === 'all'
          ? heading.dataset.defaultHeading || 'All Products'
          : button.dataset.devarchCategoryLabel || heading.dataset.defaultHeading || 'All Products';
    }
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
